import {
  BarChart3,
  ChevronDown,
  Database,
  FileSpreadsheet,
  Filter,
  FolderOpen,
  LayoutDashboard,
  Lock,
  Plus,
  RefreshCw,
  Search,
  Settings,
  ShieldCheck,
  Table2,
  Users,
} from 'lucide-react'
import type { ComponentType, ReactNode } from 'react'
import { FuseGridSurfaceShell } from './FuseGridSurfaceShell'

const navItems = [
  { label: 'Home', icon: LayoutDashboard, active: true },
  { label: 'Workbooks', icon: FileSpreadsheet },
  { label: 'Data Sources', icon: Database },
  { label: 'Visualizations', icon: BarChart3 },
  { label: 'Users', icon: Users },
  { label: 'Security', icon: ShieldCheck },
]

const fields = [
  { name: 'Cut / Day', type: '#' },
  { name: 'Hair Cut Count', type: '#' },
  { name: 'Location', type: 'Abc' },
  { name: 'Supervisor', type: 'Abc' },
  { name: 'Time Period', type: 'Date' },
  { name: 'YTD Comparison', type: '#' },
]

function SurfaceButton({
  children,
  title,
  primary = false,
}: {
  children: ReactNode
  title: string
  primary?: boolean
}) {
  return (
    <button
      type="button"
      className={primary ? 'fg-btn fg-btn-primary' : 'fg-btn'}
      title={title}
      aria-label={title}
      data-testid={primary ? 'fg-surface-primary-action' : undefined}
    >
      {children}
    </button>
  )
}

function IconText({ icon: Icon, text }: { icon: ComponentType<{ size?: number }>; text: string }) {
  return (
    <span className="fg-unified-icon-text">
      <Icon size={15} />
      <span>{text}</span>
    </span>
  )
}

function AdminSidebar() {
  return (
    <aside className="fg-admin-sidebar" aria-label="Admin navigation">
      <div className="fg-admin-sidebar-inner">
        <div className="fg-unified-site-card">
          <strong>Local Dev</strong>
          <span>site_admin_creator</span>
        </div>
        <nav className="fg-admin-nav-list">
          {navItems.map(({ label, icon: Icon, active }) => (
            <button
              type="button"
              key={label}
              className="fg-unified-nav-button"
              data-active={active ? 'true' : 'false'}
              title={`${label} navigation`}
              aria-label={`${label} navigation`}
            >
              <Icon size={15} />
              {label}
            </button>
          ))}
        </nav>
      </div>
    </aside>
  )
}

function ShellActions() {
  return (
    <>
      <SurfaceButton title="Open security settings">
        <Lock size={14} />
        Security
      </SurfaceButton>
      <SurfaceButton title="Open surface settings">
        <Settings size={14} />
        Settings
      </SurfaceButton>
    </>
  )
}

function SurfaceFixtureShell({
  kind,
  title,
  subtitle,
  sidebar,
  children,
}: {
  kind: 'admin' | 'datasource' | 'viz-authoring' | 'viz-edit'
  title: string
  subtitle: string
  sidebar?: ReactNode
  children: ReactNode
}) {
  return (
    <div data-testid="fg-surface-parity-fixture" data-surface-kind={kind}>
      <FuseGridSurfaceShell
        title={title}
        subtitle={subtitle}
        sidebar={sidebar}
        actions={<ShellActions />}
        brandHref="/local-dev-f5799da7"
        brandMarkVariant="spark"
        className="fg-unified-fixture"
      >
        {children}
      </FuseGridSurfaceShell>
    </div>
  )
}

function AdminPreview() {
  return (
    <section className="fg-unified-card" data-testid="fg-surface-admin-preview">
      <div className="fg-unified-card-header">
        <div>
          <div className="fg-unified-card-title">Admin Home</div>
          <div className="fg-subtle">Site overview, workbook access, and tenant actions keep the admin IA.</div>
        </div>
        <SurfaceButton title="Create workbook" primary>
          <Plus size={14} />
          Create Workbook
        </SurfaceButton>
      </div>
      <div className="fg-unified-metric-row">
        {[
          ['Projects', '2'],
          ['Groups', '1'],
          ['Visible Workbooks', '4'],
        ].map(([label, value]) => (
          <div className="fg-unified-metric" key={label}>
            <span>{label}</span>
            <strong>{value}</strong>
          </div>
        ))}
      </div>
      <div className="fg-unified-list">
        {['HK Test', 'Visual Vocabulary', 'Local Dev Validation Workbook'].map((name) => (
          <div className="fg-unified-row" key={name}>
            <FileSpreadsheet size={16} />
            <div>
              <strong>{name}</strong>
              <span>Personal Space · site_admin_creator</span>
            </div>
            <SurfaceButton title={`Open ${name}`}>Open</SurfaceButton>
          </div>
        ))}
      </div>
    </section>
  )
}

function DataSourcePreview() {
  return (
    <section
      className="fg-unified-card fg-datasource-surface"
      data-testid="fg-surface-datasource-preview"
    >
      <div className="fg-unified-card-header">
        <div>
          <div className="fg-unified-card-title">Data Source</div>
          <div className="fg-subtle">Relationship canvas, refresh controls, field metadata, and preview tables.</div>
        </div>
        <div className="fg-toolbar">
          <SurfaceButton title="Refresh extract">
            <RefreshCw size={14} />
            Refresh
          </SurfaceButton>
          <SurfaceButton title="Publish extract">Publish</SurfaceButton>
        </div>
      </div>
      <div className="fg-unified-datasource-layout">
        <aside className="fg-unified-source-sidebar">
          <label className="fg-unified-field-label">
            <span>Connection</span>
            <select title="Datasource connection" aria-label="Datasource connection" defaultValue="daily">
              <option value="daily">DailyRankDataModel_wHouse</option>
            </select>
          </label>
          <div className="fg-unified-source-card">
            <IconText icon={Database} text="Workbook datasource" />
            <strong>DailyRankDataModel_wHouse</strong>
            <span>Status: published</span>
          </div>
          <label className="fg-unified-check-row">
            <input type="checkbox" title="Enable schedule refresh" aria-label="Enable schedule refresh" />
            <span>Schedule refresh</span>
          </label>
        </aside>
        <div className="fg-unified-data-main">
          <div className="fg-unified-relationship-strip">
            <IconText icon={Table2} text="Relationships" />
            <span>0 table(s) · 0 relationship(s)</span>
            <SurfaceButton title="Add relationship">Add relationship</SurfaceButton>
          </div>
          <table className="fg-unified-table" aria-label="Metadata grid">
            <thead>
              <tr>
                <th>Field</th>
                <th>Datasource</th>
                <th>Type</th>
                <th>Default Agg</th>
              </tr>
            </thead>
            <tbody>
              {fields.slice(0, 4).map((field) => (
                <tr key={field.name}>
                  <td>{field.name}</td>
                  <td>DailyRankDataModel_wHouse</td>
                  <td>{field.type}</td>
                  <td>{field.type === '#' ? 'Sum' : 'Dimension'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  )
}

function VizWorkspace({ editMode }: { editMode: boolean }) {
  return (
    <section className="fg-unified-card" data-testid={editMode ? 'fg-surface-viz-edit-preview' : 'fg-surface-viz-authoring-preview'}>
      {editMode ? (
        <div className="fg-viz-edit-strip" data-testid="fg-surface-viz-edit-strip">
          <div className="fg-viz-edit-strip-title">Editing viz: Weekly Comparison</div>
          <div className="fg-viz-edit-strip-subtitle">Spreadsheet edit mode</div>
          <div className="fg-viz-edit-strip-spacer" />
          <button type="button" className="fg-viz-edit-button" title="Hide edit panels" aria-label="Hide edit panels">
            Hide edit panels
          </button>
          <button type="button" className="fg-viz-edit-button" title="Show spreadsheet bars" aria-label="Show spreadsheet bars">
            Show spreadsheet bars
          </button>
          <button type="button" className="fg-viz-edit-button fg-viz-edit-button-warning" title="Exit without saving" aria-label="Exit without saving">
            Exit without saving
          </button>
          <button type="button" className="fg-viz-edit-button fg-viz-edit-button-primary" title="Save and exit" aria-label="Save and exit">
            Save & Exit
          </button>
        </div>
      ) : null}
      <div className="fg-unified-viz-workspace" data-testid="fg-surface-viz-workspace">
        <aside className="fg-unified-field-pane">
          <label className="fg-unified-search">
            <Search size={15} />
            <input type="search" title="Search fields" aria-label="Search fields" placeholder="Search fields..." />
          </label>
          <div className="fg-unified-source-card fg-unified-source-card-selected">
            <IconText icon={Database} text="DATA SOURCE" />
            <strong>DailyRankDataModel_wHouse</strong>
          </div>
          <div className="fg-unified-field-list">
            {fields.map((field) => (
              <div className="fg-unified-field-row" key={field.name}>
                <span>{field.type}</span>
                <strong>{field.name}</strong>
              </div>
            ))}
          </div>
        </aside>
        <div className="fg-unified-authoring-pane">
          <div className="fg-unified-shelves">
            <div className="fg-unified-shelf fg-tbl-shelf-dropzone">
              <strong>Columns</strong>
              <span className="fg-tbl-pill fg-tbl-pill--discrete fg-tbl-pill--shelf">
                <span className="fg-tbl-pill__meta">Abc</span>
                <span className="fg-tbl-pill__label">Measure Names</span>
                <ChevronDown size={12} />
              </span>
              <span className="fg-tbl-pill fg-tbl-pill--discrete fg-tbl-pill--shelf">
                <span className="fg-tbl-pill__meta">Date</span>
                <span className="fg-tbl-pill__label">Time Period</span>
                <ChevronDown size={12} />
              </span>
            </div>
            <div className="fg-unified-shelf fg-tbl-shelf-dropzone">
              <strong>Rows</strong>
              <span className="fg-tbl-pill fg-tbl-pill--discrete fg-tbl-pill--shelf">
                <span className="fg-tbl-pill__meta">Abc</span>
                <span className="fg-tbl-pill__label">East/West</span>
                <ChevronDown size={12} />
              </span>
              <span className="fg-tbl-pill fg-tbl-pill--discrete fg-tbl-pill--shelf">
                <span className="fg-tbl-pill__meta">Abc</span>
                <span className="fg-tbl-pill__label">Field Supervisor</span>
                <ChevronDown size={12} />
              </span>
            </div>
          </div>
          <div className="fg-unified-viz-lower">
            <aside className="fg-unified-marks-card fg-tbl-card">
              <div className="fg-tbl-card__title">Marks</div>
              <select className="fg-tbl-select" title="Marks type" aria-label="Marks type" defaultValue="automatic">
                <option value="automatic">Automatic</option>
              </select>
              <div className="fg-unified-mark-grid fg-tbl-marks-icons">
                {['Color', 'Size', 'Label', 'Detail', 'Tooltip'].map((item) => (
                  <button className="fg-tbl-marks-icon" type="button" title={`${item} shelf`} aria-label={`${item} shelf`} key={item}>
                    {item}
                  </button>
                ))}
              </div>
              <span className="fg-tbl-pill fg-tbl-pill--continuous fg-tbl-pill--full">
                <span className="fg-tbl-pill__meta">#</span>
                <span className="fg-tbl-pill__label">Cuts / Day</span>
              </span>
              <span className="fg-tbl-pill fg-tbl-pill--continuous fg-tbl-pill--full">
                <span className="fg-tbl-pill__meta">#</span>
                <span className="fg-tbl-pill__label">Hair Cut Count</span>
              </span>
            </aside>
            <div className="fg-unified-canvas">
              <div className="fg-unified-grid-header">A</div>
              <div className="fg-unified-grid-header">B</div>
              <div className="fg-unified-grid-header">C</div>
              {Array.from({ length: 30 }, (_, index) => (
                <div key={index} className={index % 7 === 0 ? 'fg-unified-canvas-total' : undefined}>
                  {index % 7 === 0 ? 'Grand Total' : index % 3 === 0 ? 'Supervisor Total' : ''}
                </div>
              ))}
            </div>
          </div>
        </div>
        <aside className="fg-unified-filter-pane">
          <div className="fg-unified-filter-header">
            <IconText icon={Filter} text="Controls" />
            <SurfaceButton title="Hide controls">Hide</SurfaceButton>
          </div>
          {['Supervisor', 'Location', 'East/West'].map((filter) => (
            <label className="fg-unified-field-label" key={filter}>
              <span>{filter}</span>
              <select title={`${filter} filter`} aria-label={`${filter} filter`} defaultValue="all">
                <option value="all">(All)</option>
              </select>
            </label>
          ))}
        </aside>
      </div>
    </section>
  )
}

export function FuseGridSurfaceAdminFixture() {
  return (
    <SurfaceFixtureShell
      kind="admin"
      title="Admin Home"
      subtitle="Separate admin route with shared FuseGrid chrome language"
      sidebar={<AdminSidebar />}
    >
      <div className="fg-unified-page-header">
        <div>
          <h1>Admin Surface</h1>
          <p>Admin remains its own navigation surface; only the visual system is shared.</p>
        </div>
        <SurfaceButton title="Open workbook browser" primary>
          <FolderOpen size={14} />
          Open Workbook
        </SurfaceButton>
      </div>
      <AdminPreview />
    </SurfaceFixtureShell>
  )
}

export function FuseGridSurfaceDataSourceFixture() {
  return (
    <SurfaceFixtureShell
      kind="datasource"
      title="Data Source"
      subtitle="Separate datasource route with shared panels, buttons, and chrome tokens"
    >
      <div className="fg-unified-page-header">
        <div>
          <h1>Data Source Surface</h1>
          <p>Connections, relationships, refresh, preview, and metadata stay in the datasource workflow.</p>
        </div>
        <SurfaceButton title="Add relationship" primary>
          <Plus size={14} />
          Add Relationship
        </SurfaceButton>
      </div>
      <DataSourcePreview />
    </SurfaceFixtureShell>
  )
}

export function FuseGridSurfaceVizAuthoringFixture() {
  return (
    <SurfaceFixtureShell
      kind="viz-authoring"
      title="Weekly Comparison"
      subtitle="Separate visualization authoring surface with shared controls and panel language"
    >
      <div className="fg-unified-page-header">
        <div>
          <h1>Viz Authoring Surface</h1>
          <p>Fields, shelves, marks, filters, and canvas remain a dedicated authoring experience.</p>
        </div>
        <SurfaceButton title="Save visualization" primary>
          Save Visualization
        </SurfaceButton>
      </div>
      <VizWorkspace editMode={false} />
    </SurfaceFixtureShell>
  )
}

export function FuseGridSurfaceVizEditFixture() {
  return (
    <SurfaceFixtureShell
      kind="viz-edit"
      title="Weekly Comparison"
      subtitle="Separate viz spreadsheet-edit mode with the compact contextual strip"
    >
      <VizWorkspace editMode />
    </SurfaceFixtureShell>
  )
}
